/** @jsxImportSource @emotion/react */

import { EuiLink, EuiText } from "@inscopix/ideas-eui";
import { captureException } from "@sentry/react";
import {
  ColDef,
  ICellRendererParams,
  IDetailCellRendererParams,
  ValueFormatterParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-enterprise";

import { ButtonViewTaskLogs } from "components/ButtonViewTaskLogs/ButtonViewTaskLogs";
import { TaskStatusBadge } from "components/TaskStatusBadge/TaskStatusBadge";
import { UserAvatarRenderer } from "components/UserAvatarRenderer/UserAvatarRenderer";
import {
  ApplicationUser,
  File as DrsFile,
  GdtAnalysisTable,
  GdtAnalysisTableRow,
  Interval,
  Task,
} from "graphql/_Types";
import { mapValues } from "lodash";
import moment from "moment";
import { useMemo } from "react";
import { formatDate } from "utils/formatDate";
import { TaskStatus } from "types/constants";
import { roundToSignificant } from "utils/roundToSignificant";
import { Tooltip } from "components/Tooltip/Tooltip";
import { TasksTableDetailRenderer } from "./TasksTableDetailRenderer";
import { isNonNullish } from "utils/isNonNullish";
import { ShortTaskId } from "components/ShortTaskId/ShortTaskId";
import { CellValueRendererRowIdentifier } from "pages/gdt/cell-value-renderers/CellValueRendererRowIdentifier";
import { RowIdentifier } from "@inscopix/ideas-hyperformula";
import { useDataTableContext } from "pages/gdt/store/DataTableProvider";
import { agGridDateComparator } from "utils/agGridDateComparator";

export const TASK_TABLE_DETAIL_RENDERER_HEIGHT = 400;

export type TasksTableTask = Pick<
  Task,
  "id" | "status" | "credits" | "dateCreated" | "cloned"
> & {
  duration?: {
    readableString: string;
    interval: Pick<Interval, "days" | "hours" | "minutes" | "seconds">;
  };
  toolName: string;
  table?: Pick<GdtAnalysisTable, "id" | "name" | "key"> & {
    row: Pick<GdtAnalysisTableRow, "id" | "index"> & {
      deleted: boolean;
    };
    resultsByKey?: Record<string, DrsFile["id"]>;
  };
  isSystemUtil: boolean;
  user: Pick<ApplicationUser, "id" | "firstName" | "lastName"> | null;
};

export interface TasksTableProps {
  tasks: TasksTableTask[];
  suppressTableLinks?: boolean;
}

export const TasksTable = ({ tasks, suppressTableLinks }: TasksTableProps) => {
  const setSelectedTableId = useDataTableContext((s) => s.setSelectedTableId);

  const totalCredits = tasks.reduce(
    // don't include cloned tasks in the total credits
    (sum, { credits, cloned }) => (sum += cloned ? 0 : (credits ?? 0)),
    0,
  );

  const rowData = useMemo(() => [...tasks], [tasks]);

  const columnDefs: ColDef<TasksTableTask>[] = useMemo(
    () => [
      {
        headerName: "Date",
        width: 170,
        sortable: true,
        sortingOrder: ["desc", "asc", null],
        valueGetter: ({ data }) => {
          return formatDate(data?.dateCreated);
        },
        cellRenderer: "agGroupCellRenderer",
        filter: "agDateColumnFilter",
        filterParams: {
          comparator: agGridDateComparator,
        },
      },
      {
        headerName: "User",
        width: 70,
        resizable: false,
        cellRenderer: ({ data }: ICellRendererParams<TasksTableTask>) => {
          if (data === undefined) {
            captureException("Data should be defined in grid");
            return null;
          }
          if (data.user === null) {
            return null;
          }

          return <UserAvatarRenderer userId={data.user.id} />;
        },
        sortable: true,
        valueGetter: ({ data }) =>
          data?.user ? `${data.user.firstName} ${data.user.lastName}` : "",
        filter: "agSetColumnFilter",
      },
      {
        headerName: "Source",
        flex: 2,
        minWidth: 150,
        sortable: true,
        valueGetter: ({ data }) => data?.table?.name ?? "",
        filter: "agSetColumnFilter",
        cellRenderer: ({ data }: ICellRendererParams<TasksTableTask>) => {
          if (data === undefined) {
            captureException("Data should be defined in grid");
            return null;
          }
          const table = data.table;
          if (table === undefined) {
            return (
              <EuiText color={data.isSystemUtil ? "text" : "subdued"} size="xs">
                {data.toolName}
              </EuiText>
            );
          }
          return (
            <Tooltip
              content={
                table?.row.deleted
                  ? "This task has been deleted from the analysis table"
                  : ""
              }
            >
              {suppressTableLinks ? (
                table.name
              ) : (
                <EuiLink
                  color={table.row.deleted ? "subdued" : undefined}
                  onClick={() => setSelectedTableId(table.id)}
                >
                  {table.name}
                </EuiLink>
              )}
            </Tooltip>
          );
        },
      },
      {
        headerName: "Row ID",
        flex: 1,
        maxWidth: 300,
        minWidth: 100,
        valueGetter: ({ data }) => {
          const table = data?.table;
          if (table === undefined) {
            return "";
          }
          return table.key + "-" + String(table.row.index);
        },
        cellRenderer: ({ data }: ICellRendererParams<TasksTableTask>) => {
          if (data === undefined) {
            captureException("Data should be defined in grid");
            return null;
          }
          if (data.table === undefined) {
            return null;
          }
          return (
            <CellValueRendererRowIdentifier
              rowIdentifier={
                new RowIdentifier(data.table.key, data.table.row.index)
              }
            />
          );
        },
      },
      {
        headerName: "Status",
        width: 120,
        resizable: false,
        sortable: true,
        valueGetter: ({ data }) =>
          data?.status !== undefined ? TaskStatus[data?.status] : "",
        cellRenderer: ({ data }: ICellRendererParams<TasksTableTask>) => {
          if (data === undefined) {
            captureException("Data should be defined in grid");
            return null;
          }
          return <TaskStatusBadge taskStatus={data.status} />;
        },
        filter: "agSetColumnFilter",
      },
      {
        headerName: "Log",
        width: 70,
        resizable: false,
        cellRenderer: ({ data }: ICellRendererParams<TasksTableTask>) => {
          if (data === undefined) {
            captureException("Data should be defined in grid");
            return null;
          }
          return (
            <ButtonViewTaskLogs
              taskId={data.id}
              taskStatus={data.status}
              taskCreated={data.dateCreated}
            />
          );
        },
        filter: false,
        sortable: false,
      },
      {
        headerName: "Task ID",
        flex: 1,
        maxWidth: 200,
        minWidth: 75,
        valueGetter: ({ data }) => data?.id ?? "",
        cellRenderer: ({ data }: ICellRendererParams<TasksTableTask>) => {
          if (data === undefined) {
            captureException("Data should be defined in grid");
            return null;
          }

          return <ShortTaskId taskId={data.id} />;
        },
      },
      {
        headerName: "Duration",
        flex: 0.5,
        minWidth: 95,
        maxWidth: 150,
        cellRenderer: ({ data }: ICellRendererParams<TasksTableTask>) => {
          if (data === undefined) {
            captureException("Data should be defined in grid");
            return null;
          }
          return <span>{data.duration?.readableString}</span>;
        },
        sortable: true,
        valueGetter: ({ data }) =>
          moment
            .duration(mapValues(data?.duration?.interval, (val) => val ?? 0))
            .asSeconds(),
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Compute Credits",
        width: 180,
        pinned: "right",
        sortable: true,
        valueGetter: ({ data }) => {
          if (data === undefined) {
            captureException("Data should be defined in grid");
          }

          return data?.credits ?? 0;
        },
        valueFormatter: ({
          data,
          value,
        }: ValueFormatterParams<TasksTableTask, number>) => {
          if (isNonNullish(value)) {
            const credits = roundToSignificant(value).toString();
            const cloned = data?.cloned;
            return cloned ? `[${credits}]` : credits;
          }
          return "";
        },
        cellStyle: ({ data }) => {
          if (data?.cloned) {
            // format cloned task compute credit
            return { color: "grey", fontStyle: "italic" };
          }
          return null;
        },
        tooltipValueGetter: ({ data }) =>
          data?.cloned ? "Copied tasks do not count towards usage" : "",
        filter: "agNumberColumnFilter",
      },
    ],
    [setSelectedTableId, suppressTableLinks],
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      filter: "agTextColumnFilter",
      suppressHeaderMenuButton: false,
      suppressHeaderContextMenu: true,
      menuTabs: ["filterMenuTab" as const],
    }),
    [],
  );

  return (
    <AgGridReact<TasksTableTask>
      className="ag-theme-balham-cell-borders"
      css={{ width: "100%" }}
      statusBar={{
        statusPanels: [
          {
            key: "credits",
            align: "right",
            statusPanel: () => {
              return (
                <div css={{ padding: "5px 0px" }}>
                  <strong>
                    {roundToSignificant(totalCredits)} compute credits
                  </strong>
                </div>
              );
            },
          },
        ],
      }}
      rowData={rowData}
      rowHeight={35}
      defaultColDef={defaultColDef}
      enableRangeSelection
      columnDefs={columnDefs}
      masterDetail={true}
      isRowMaster={(task) =>
        task.table !== undefined && !task.table.row.deleted
      }
      detailCellRenderer={(
        params: IDetailCellRendererParams<TasksTableTask>,
      ) => {
        const table = params.data?.table;
        if (params.data === undefined || table === undefined) {
          return null;
        }
        return (
          <TasksTableDetailRenderer rowId={table.row.id} tableId={table.id} />
        );
      }}
      detailRowHeight={TASK_TABLE_DETAIL_RENDERER_HEIGHT}
    />
  );
};
